import _ from 'underscore';
import { RANGES } from './constants';
import {
  DateRange, PrimaryDateRangesParams,
} from '../types';

/**
 * Gets the date ranges that can be selected in the main date range panel.
 * @param {Object} putil - Date utilities
 * @param { Date } today - Today's date. This is used to generate the date ranges correctly.
 * @param {Object} i18n - i18n object for the date range strings
 * @param {Date} customStart - The start date for the custom date range
 * @param {Date} customEnd -  The end date for the custom date range
 * @returns {Object} dateRanges - The object keys are the date range keys
 * and the value will have following properties:
 * start - The start date for the custom date range period
 * end - The end date for the custom date range period
 * text - The text for the custom date range period
 */
export function primaryDateRanges({
  putil,
  today,
  i18n,
  customStart = putil.lastNDate(today, 7),
  customEnd = putil.lastNDate(today, 1),
}: PrimaryDateRangesParams): Record<string, DateRange> {
  const dateRanges: Record<string, DateRange> = {
    [RANGES.CUSTOM]: {
      grain: 4,
      text: i18n.getString('DateFilter_Option_Custom'),
      start: customStart,
      end: customEnd,
      isCustom: true,
    },
    [RANGES.CUSTOM_TIME]: {
      grain: 23,
      text: i18n.getString('DateFilter_Option_CustomTime'),
      start: customStart,
      end: customEnd,
      isCustom: true,
    },
    [RANGES.TODAY]: {
      grain: 3,
      text: i18n.getString('DateFilter_Option_Today'),
      start: today,
      end: today,
    },
    [RANGES.YESTERDAY]: {
      grain: 1,
      text: i18n.getString('DateFilter_Option_Yesterday'),
      start: putil.lastNDate(today, 1),
      end: putil.lastNDate(today, 1),
    },
    [RANGES.THIS_WEEK_SUNDAY]: {
      grain: 7,
      text: i18n.getString('DateFilter_Option_ThisWeekSunday'),
      start: putil.lastNDate(today, today.getDay()),
      end: today,
    },
    [RANGES.THIS_WEEK_MONDAY]: {
      grain: 8,
      text: i18n.getString('DateFilter_Option_ThisWeekMonday'),
      start: today.getDay() === 0
        ? putil.lastNDate(today, 6)
        : putil.lastNDate(today, today.getDay() - 1),
      end: today,
    },
    [RANGES.LAST_SEVEN_DAYS]: {
      grain: 2,
      text: i18n.getString('DateFilter_Option_Last7days'),
      start: putil.lastNDate(today, 7),
      end: putil.lastNDate(today, 1),
    },
    [RANGES.LAST_WEEK_SUNDAY]: {
      grain: 9,
      text: i18n.getString('DateFilter_Option_LastWeekSunday'),
      start: putil.lastNDate(today, today.getDay() + 7),
      end: putil.lastNDate(today, today.getDay() + 1),
    },
    [RANGES.LAST_WEEK_MONDAY]: {
      grain: 14,
      text: i18n.getString('DateFilter_Option_LastWeekMonday'),
      start: putil.lastNDate(today, today.getDay() + 6),
      end: putil.lastNDate(today, today.getDay()),
    },
    [RANGES.LAST_FOURTEEN_DAYS]: {
      grain: 6,
      text: i18n.getString('DateFilter_Option_Last14Days'),
      start: putil.lastNDate(today, 14),
      end: putil.lastNDate(today, 1),
    },
    [RANGES.THIS_MONTH]: {
      grain: 10,
      text: i18n.getString('DateFilter_Option_ThisMonth'),
      start: putil.firstDateNMonthsAgo(today),
      end: today,
    },
    [RANGES.LAST_MONTH]: {
      grain: 11,
      text: i18n.getString('DateFilter_Option_LastMonth'),
      start: putil.firstDateNMonthsAgo(today, 1),
      end: putil.lastNDate(putil.firstDateNMonthsAgo(today), 1),
    },
    [RANGES.LAST_THIRTY_DAYS]: {
      grain: 5,
      text: i18n.getString('DateFilter_Option_Last30Days'),
      start: putil.lastNDate(today, 30),
      end: putil.lastNDate(today, 1),
    },
    [RANGES.LAST_THREE_MONTHS]: {
      grain: 12,
      text: i18n.getString('DateFilter_Option_Last3Months'),
      start: putil.firstDateNMonthsAgo(today, 3),
      end: putil.lastNDate(putil.firstDateNMonthsAgo(today), 1),
    },
    [RANGES.LAST_SIX_MONTHS]: {
      grain: 13,
      text: i18n.getString('DateFilter_Option_Last6Months'),
      start: putil.firstDateNMonthsAgo(today, 6),
      end: putil.lastNDate(putil.firstDateNMonthsAgo(today), 1),
    },
    [RANGES.TWELVE_MONTHS_TO_DATE]: {
      grain: 15,
      text: i18n.getString('DateFilter_Option_TwelveMonthsToDate'),
      start: putil.todayNYearsAgo(today, 1),
      end: today,
    },
    [RANGES.THIS_YEAR]: {
      grain: 20,
      text: i18n.getString('DateFilter_Option_ThisYear'),
      start: putil.firstDateNYearsAgo(today),
      end: today,
    },
    [RANGES.LAST_YEAR]: {
      grain: 21,
      text: i18n.getString('DateFilter_Option_LastYear'),
      start: putil.firstDateNYearsAgo(today, 1),
      end: putil.lastNDate(putil.firstDateNYearsAgo(today), 1),
    },
    [RANGES.ALL_TIME]: {
      grain: 22,
      text: i18n.getString('DateFilter_Option_AllTime'),
      start: putil.todayNYearsAgo(today, 3),
      end: today,
    },
    [RANGES.ALL_TIME_FIVE_YEARS]: {
      grain: 24,
      text: i18n.getString('DateFilter_Option_AllTime'),
      start: putil.todayNYearsAgo(today, 5),
      end: today,
    },
  };

  _.each(dateRanges, (range, key) => _.extend(range, { key }));
  return dateRanges;
}
